<template>
  <section
    data-testid="feedback-panel"
    class="rounded-md border border-gray-200 bg-white"
  >
    <header class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
      <h3 class="text-base font-semibold text-gray-900">Customer feedback</h3>
      <span
        v-if="feedback?.flag"
        class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
        :class="flagPillClass"
        data-testid="feedback-panel-flag"
      >
        {{ flagLabel }}
      </span>
    </header>

    <div v-if="loading" class="px-4 py-6 text-sm text-gray-500">Loading feedback…</div>

    <div v-else-if="!feedback" class="px-4 py-6 text-sm text-gray-500">
      No feedback yet for this order.
      <span class="text-xs text-gray-400 block mt-1">
        A survey is sent automatically 24 hours after delivery.
      </span>
    </div>

    <div v-else class="px-4 py-3 space-y-4">
      <div v-if="!feedback.responded_at" class="text-sm text-gray-600">
        Survey sent {{ formatRelative(feedback.survey_sent_at) }} — awaiting customer response.
        <span v-if="feedback.reminder_sent_at" class="text-xs text-gray-400 block mt-0.5">
          Reminder sent {{ formatRelative(feedback.reminder_sent_at) }}.
        </span>
      </div>

      <div v-else class="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div data-testid="feedback-panel-overall">
          <div class="text-xs text-gray-500 uppercase tracking-wide">Overall</div>
          <div class="flex items-center gap-0.5 mt-1">
            <StarIcon
              v-for="n in 5"
              :key="`overall-${n}`"
              class="size-4"
              :class="(feedback.overall_rating ?? 0) >= n ? 'text-amber-400' : 'text-gray-200'"
            />
          </div>
        </div>
        <div data-testid="feedback-panel-quality">
          <div class="text-xs text-gray-500 uppercase tracking-wide">Quality</div>
          <div class="flex items-center gap-0.5 mt-1">
            <StarIcon
              v-for="n in 5"
              :key="`quality-${n}`"
              class="size-4"
              :class="(feedback.quality_rating ?? 0) >= n ? 'text-amber-400' : 'text-gray-200'"
            />
          </div>
        </div>
        <div data-testid="feedback-panel-delivery">
          <div class="text-xs text-gray-500 uppercase tracking-wide">Delivery</div>
          <div class="flex items-center gap-0.5 mt-1">
            <StarIcon
              v-for="n in 5"
              :key="`delivery-${n}`"
              class="size-4"
              :class="(feedback.delivery_rating ?? 0) >= n ? 'text-amber-400' : 'text-gray-200'"
            />
          </div>
        </div>
        <div v-if="feedback.nps_score !== null" data-testid="feedback-panel-nps">
          <div class="text-xs text-gray-500 uppercase tracking-wide">NPS</div>
          <div class="text-base font-semibold mt-1">{{ feedback.nps_score }} / 10</div>
        </div>
      </div>

      <div v-if="feedback.responded_at && feedback.comment" class="bg-amber-50 border-l-4 border-amber-400 px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap break-words">
        {{ feedback.comment }}
      </div>

      <div v-if="feedback.responded_at" class="text-xs text-gray-400">
        Survey sent {{ formatRelative(feedback.survey_sent_at) }} ·
        Responded {{ formatRelative(feedback.responded_at) }}
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
  import { computed, onMounted, watch } from 'vue';
  import { StarIcon } from '@heroicons/vue/24/solid';
  import { useCustomerFeedback, type FeedbackRecord } from '@/composables/useCustomerFeedback';
  import { formatDistanceToNowStrict, parseISO } from 'date-fns';

  const props = defineProps<{ orderId: number | null | undefined }>();

  const { orderFeedback, orderFeedbackLoading, fetchForOrder } = useCustomerFeedback();

  const feedback = computed<FeedbackRecord | null>(() => orderFeedback.value);
  const loading = computed(() => orderFeedbackLoading.value);

  const flagLabel = computed(() => {
    switch (feedback.value?.flag) {
      case 'at_risk':
        return 'At Risk';
      case 'happy':
        return 'Happy Customer';
      case 'neutral':
        return 'Neutral';
      default:
        return '';
    }
  });

  const flagPillClass = computed(() => {
    switch (feedback.value?.flag) {
      case 'at_risk':
        return 'bg-red-100 text-red-700';
      case 'happy':
        return 'bg-green-100 text-green-700';
      case 'neutral':
        return 'bg-gray-100 text-gray-600';
      default:
        return '';
    }
  });

  function formatRelative(iso: string | null): string {
    if (!iso) return '—';
    try {
      return formatDistanceToNowStrict(parseISO(iso), { addSuffix: true });
    } catch {
      return iso;
    }
  }

  function refresh() {
    if (props.orderId) {
      void fetchForOrder(props.orderId);
    }
  }

  watch(() => props.orderId, refresh);
  onMounted(refresh);
</script>
